import BaseController from '@humandigital-agency/base-controller';
import delegate from 'delegate';

class Search {
    constructor(container) {
        this.container = container;
        this.form = null;
        this.formInput = null;
        this.searchResultsContainer = null;

        this.apiEndpoint = `/umbraco/api/search/suggestions?filter=`;

        this.nodeId = document.body.getAttribute('nodeid');

        this.abortController = null; // Will contain an AbortController when making a new API call
        this.waiterTime = 250; // Time in MS to wait with making a new API call
        this.changeWaiter = null; // Clearable timeout handler
        this.minInput = 4; // Minimal amount of characters before a search is triggered
    }

    init() {
        this.form = this.container.querySelector(`form`) || this.container;
        if (!this.form) {
            console.log(`Form not found`);
            return;
        }
        this.formInput = this.form.querySelector(`input[type="search"]`);
        if (!this.formInput) {
            console.log(`Forminput not found`);
            return;
        }

        if ('AbortController' in window) {
            this.abortController = new AbortController();
        }

        this.createSearchContainer();
        this.setEventListeners();
    }

    setEventListeners() {
        // Typing/updating input field
        this.formInput.addEventListener(
            'input',
            this.handleInputChange.bind(this)
        );

        // Submitting
        this.form.addEventListener('submit', (e) => {
            if (!this.formInput.value.trim()) {
                e.preventDefault();
                this.formInput.focus();
                return false;
            }
            return true;
        });

        // Navigating results
        this.formInput.addEventListener('keydown', (e) => {
            if (e.key.toLowerCase() == 'arrowdown') {
                e.preventDefault();
                this.searchResultsContainer.querySelector('li a').focus();
            }
        });

        delegate(`.searchForm ul.results a`, `keydown`, (e) => {
            if (e.key.toLowerCase() == 'arrowup') {
                e.preventDefault();
                this.arrowUp(e.delegateTarget.parentNode);
            } else if (e.key.toLowerCase() == 'arrowdown') {
                e.preventDefault();
                this.arrowDown(e.delegateTarget.parentNode);
            }
        });

        document.addEventListener('keydown', e => {
			if (e.key.toLowerCase().substr(0, 3) == 'esc') {
                this.resetResults();
            }
        });
    }

    arrowDown(el) {
        if (el.nextElementSibling && el.nextElementSibling.querySelector('a')) {
            el.nextElementSibling.querySelector('a').focus();
        }
    }

    arrowUp(el) {
        if (el.previousElementSibling && el.previousElementSibling.querySelector('a')) {
            el.previousElementSibling.querySelector('a').focus();
        } else {
            this.formInput.focus();
        }
    }

    handleInputChange() {
        const query = this.formInput.value.trim();

        if (query && query.length >= this.minInput) {
            // Update status button to show
            this.updateResults(query);
        } else if (query) {
            // User entered less than {this.minInput} characters
        } else {
            this.resetResults();
        }
    }

    updateResults(query) {
        // Reset waiter to prevent unnessecary calls
        if (this.changeWaiter !== null) {
            clearTimeout(this.changeWaiter);
        }

        this.changeWaiter = setTimeout(() => {
            // If there's a call going on, abort it and create a new abortcontroller to reuse
            if ('AbortController' in window && this.abortController) {
                this.abortController.abort();
                this.abortController = new AbortController();
            }

            // Fetch new results
            this.fetchResults(query)
                .then((data) => {
                    this.insertSearchSuggestions(data);
                })
                .catch((e) => {
                    console.error(e);
                });

            this.changeWaiter = null;
        }, this.waiterTime);
    }

    insertSearchSuggestions(data) {
        if (data.suggestions && data.suggestions.length) {
            this.searchResultsContainer.innerHTML = '';

            data.suggestions.forEach((suggestionObject) => {
                const listItem = document.createElement(`li`);
                const link = document.createElement(`a`);
                const linkText = document.createElement(`span`);
                link.href = suggestionObject.url;
                link.title = `Ga naar de pagina ${suggestionObject.title}`;
                linkText.innerText = suggestionObject.title;

                link.appendChild(linkText);
                listItem.appendChild(link);
                this.searchResultsContainer.appendChild(listItem);
            });
        }
    }

    fetchResults(query) {
        let options = {};
        if ('AbortController' in window && this.abortController) {
            options.signal = this.abortController.signal;
            options.headers = {
                "HD_UMB_NodeId": this.nodeId
            };
        }

        return fetch(
            `${this.apiEndpoint}${encodeURIComponent(query)}`,
            options
        ).then((response) => {
            return response.json();
        });
    }

    resetResults() {
        // Cancel any ongoing call
        if ('AbortController' in window && this.abortController) {
            this.abortController.abort();
            this.abortController = new AbortController();
        }

        this.searchResultsContainer.innerHTML = '';
    }

    createSearchContainer() {
        this.searchResultsContainer = document.createElement(`ul`);
        this.searchResultsContainer.classList.add(`results`);
        this.container.appendChild(this.searchResultsContainer);
    }
}

export default class SearchController extends BaseController {
    constructor() {
        super('.searchForm', Search);
    }
}
